import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import Logo from '../Logo/Logo';
import { getSolutionColorByPath } from '../../styles/solutions';
import { darken } from '../../styles/colors';

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid ${theme.colors.neutral.gray200};
  z-index: ${theme.zIndex.sticky};
  transition: all ${theme.transitions.normal};

  &:hover {
    background: ${theme.colors.neutral.white};
  }
`;

const Nav = styled.nav`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 ${theme.spacing.lg};
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 80px;

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: 0 ${theme.spacing.md};
    height: 70px;
  }
`;

const LogoButton = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  color: ${theme.colors.primary.data};
  transition: transform ${theme.transitions.fast};

  &:hover {
    transform: scale(1.05);
  }

  svg {
    height: 36px;
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    svg {
      height: 28px;
    }
  }
`;

const NavLinks = styled.div<{ isOpen: boolean }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xl};

  @media (max-width: ${theme.breakpoints.md}) {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: ${theme.colors.neutral.white};
    border-bottom: 1px solid ${theme.colors.neutral.gray200};
    flex-direction: column;
    padding: ${theme.spacing.lg};
    gap: ${theme.spacing.lg};
    transform: translateY(${props => props.isOpen ? '0' : '-100%'});
    opacity: ${props => props.isOpen ? '1' : '0'};
    visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
    transition: all ${theme.transitions.normal};
  }
`;

const NavLink = styled.a<{ $accent: string }>`
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.neutral.gray700};
  transition: color ${theme.transitions.fast};
  position: relative;
  cursor: pointer;

  &:hover {
    color: ${props => props.$accent};
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -4px;
    left: 0;
    width: 0;
    height: 2px;
    background: ${props => props.$accent};
    transition: width ${theme.transitions.normal};
  }

  &:hover::after {
    width: 100%;
  }
`;

const CTAButton = styled.button<{ $accent: string }>`
  background: ${props => props.$accent};
  color: ${theme.colors.neutral.white};
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.lg};
  font-weight: ${theme.typography.fontWeight.semibold};
  font-size: ${theme.typography.fontSize.sm};
  transition: all ${theme.transitions.normal};
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &:hover {
    background: ${props => darken(props.$accent)};
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: ${theme.breakpoints.md}) {
    width: 100%;
    padding: ${theme.spacing.md} ${theme.spacing.lg};
  }
`;

const MenuToggle = styled.button`
  display: none;
  color: ${theme.colors.neutral.gray700};
  font-size: ${theme.typography.fontSize['2xl']};
  padding: ${theme.spacing.xs};

  @media (max-width: ${theme.breakpoints.md}) {
    display: block;
  }
`;

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Logomarca, links e botão do cabeçalho assumem a cor da solução aberta.
  const accent = getSolutionColorByPath(location.pathname);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const scrollToSection = (sectionId: string) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    closeMenu();
  };

  const goToHome = () => {
    navigate('/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
    closeMenu();
  };

  const goToContact = () => {
    navigate('/contato');
    window.scrollTo({ top: 0, behavior: 'smooth' });
    closeMenu();
  };

  const goToTop = () => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    closeMenu();
  };

  return (
    <HeaderContainer>
      <Nav>
        <LogoButton onClick={goToHome}>
          <Logo accentColor={accent} />
        </LogoButton>

        <NavLinks isOpen={isMenuOpen}>
          <NavLink $accent={accent} onClick={goToTop}>
            Início
          </NavLink>
          <NavLink $accent={accent} onClick={() => scrollToSection('solutions')}>
            Soluções
          </NavLink>
          <NavLink $accent={accent} onClick={() => scrollToSection('clients')}>
            Clientes
          </NavLink>
          <CTAButton $accent={accent} onClick={goToContact}>
            Fale Conosco
          </CTAButton>
        </NavLinks>

        <MenuToggle onClick={toggleMenu}>
          {isMenuOpen ? '✕' : '☰'}
        </MenuToggle>
      </Nav>
    </HeaderContainer>
  );
};

export default Header; 